import { useEffect, useMemo, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import moment from "moment"

import Input from "components/forms/input"
import Label from "components/forms/label"

import { useStockOpnameVarianceRepositories } from "repositories/stock-opname"

const formatNumber = (value) => {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("id-ID")
}

const formatMoney = (value) => {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("id-ID", { maximumFractionDigits: 0 })
}

const FILTER_OPTIONS = [
  { id: "diff", name: "Hanya selisih ≠ 0" },
  { id: "all", name: "Semua item" },
  { id: "lebih", name: "Lebih (fisik > sistem)" },
  { id: "kurang", name: "Kurang (fisik < sistem)" },
  { id: "transacted", name: "Ada transaksi saat freeze" },
]

const PER_PAGE_OPTIONS = [50, 100, 200, 500]

export default function Variance() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const filter = searchParams.get("filter") || "diff"
  const page = Number(searchParams.get("page") || 1)
  const perPage = Number(searchParams.get("per_page") || 50)
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "")
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput.trim())

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350)
    return () => clearTimeout(handle)
  }, [searchInput])

  useEffect(() => {
    const current = searchParams.get("search") || ""
    if (current !== debouncedSearch) {
      const next = Object.fromEntries(searchParams.entries())
      if (debouncedSearch) next.search = debouncedSearch
      else delete next.search
      next.page = "1"
      setSearchParams(next, { replace: true })
    }
  }, [debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, isValidating } = useStockOpnameVarianceRepositories(id, {
    filter,
    search: debouncedSearch || undefined,
    page,
    per_page: perPage,
  })

  const freeze = data?.freeze
  const rows = useMemo(() => data?.rows || [], [data])
  const summaryAll = data?.summary || {}
  const summaryFiltered = data?.filtered_summary || {}
  const meta = data?.meta || { page: 1, per_page: perPage, last_page: 1, total: 0, from: 0, to: 0 }

  const mergeParams = (next) => {
    const merged = Object.fromEntries(searchParams.entries())
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") delete merged[k]
      else merged[k] = String(v)
    })
    setSearchParams(merged)
  }

  if (isLoading) {
    return <div className="p-10 text-sm text-center text-neutral-500">Memuat data selisih...</div>
  }

  const pageNumbers = buildPageList(meta.page, meta.last_page)

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Link to="/stock-opname" className="hover:underline">Stock Opname</Link>
          <span>/</span>
          <Link to={`/stock-opname/${id}`} className="hover:underline">{freeze?.freeze_no}</Link>
          <span>/</span>
          <span>Cek Selisih</span>
        </div>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Cek Selisih</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Outlet <strong>{freeze?.outlet_name || freeze?.outlet_id}</strong>
              {" · "}Dibekukan {freeze?.frozen_at ? moment(freeze.frozen_at).format("DD/MM/YYYY HH:mm") : "-"}
            </p>
          </div>
          <a
            href={`${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/variance/export`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-emerald-700 border rounded-lg bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          >
            ↓ Unduh Laporan Excel
          </a>
        </div>

        <div className="mt-5">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-2">
            Total seluruh stock (sebelum filter)
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SummaryCard label="Komputer (qty)" value={formatNumber(summaryAll.total_computer)} />
            <SummaryCard label="Fisik (qty)" value={formatNumber(summaryAll.total_fisik)} />
            <SummaryCard label="Lebih (qty)" value={formatNumber(summaryAll.total_lebih_qty)} tone="lebih" />
            <SummaryCard label="Kurang (qty)" value={formatNumber(summaryAll.total_kurang_qty)} tone="kurang" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3 md:grid-cols-4">
            <SummaryCard label="Nilai Komputer" value={formatMoney(summaryAll.total_nilai_computer)} />
            <SummaryCard label="Nilai Fisik" value={formatMoney(summaryAll.total_nilai_fisik)} />
            <SummaryCard label="Nilai Lebih" value={formatMoney(summaryAll.total_nilai_lebih)} tone="lebih" />
            <SummaryCard label="Nilai Kurang" value={formatMoney(summaryAll.total_nilai_kurang)} tone="kurang" />
          </div>
        </div>
      </div>

      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-3">
            <Label value="Filter" />
            <select
              value={filter}
              onChange={(e) => mergeParams({ filter: e.target.value, page: 1 })}
              className="block w-full px-3 py-2 text-sm border rounded-lg border-neutral-300"
            >
              {FILTER_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-7">
            <Label value="Cari produk" />
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Nama produk / SKU / model..."
            />
          </div>
          <div className="md:col-span-2">
            <Label value="Per halaman" />
            <select
              value={perPage}
              onChange={(e) => mergeParams({ per_page: e.target.value, page: 1 })}
              className="block w-full px-3 py-2 text-sm border rounded-lg border-neutral-300"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 text-xs text-neutral-500">
          {meta.total > 0
            ? `Menampilkan ${meta.from}–${meta.to} dari ${formatNumber(meta.total)} item${isValidating ? " · memuat..." : ""}`
            : `Tidak ada item sesuai filter${isValidating ? " · memuat..." : ""}`}
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr className="text-left text-neutral-600">
                <th className="px-3 py-3 font-medium">No</th>
                <th className="px-3 py-3 font-medium">Kode / Produk</th>
                <th className="px-3 py-3 font-medium text-right">Komputer</th>
                <th className="px-3 py-3 font-medium text-right">Nilai Komputer</th>
                <th className="px-3 py-3 font-medium text-right">Fisik</th>
                <th className="px-3 py-3 font-medium text-right">Nilai Fisik</th>
                <th className="px-3 py-3 font-medium text-right text-emerald-700">Lebih</th>
                <th className="px-3 py-3 font-medium text-right text-rose-700">Kurang</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-neutral-500">
                    Tidak ada data sesuai filter.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.product_unit_id} className="border-t border-neutral-100 hover:bg-brand-50 transition-colors">
                    <td className="px-3 py-2 text-neutral-500">{meta.from + idx}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900">{r.product_unit_name}</div>
                      <div className="text-xs text-neutral-500">
                        {r.product_unit_id}
                        {r.product_model && <span className="ml-1">· {r.product_model}</span>}
                        {!r.in_snapshot && <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-700 border border-amber-200">Baru</span>}
                        {(() => {
                          // "Terjual" = qty dari Order yg PAID/CONFIRMED/DONE selama freeze
                          // (sumber: backend.sold_pcs). Beda dengan delta current_stock yg
                          // juga turun saat order PENDING_CONFIRMATION (stok di-reserve).
                          const soldPcs = Number(r.sold_pcs || 0)
                          const soldLocal = Number(r.sold_pcs_local || 0)
                          const soldYoiboi = Number(r.sold_pcs_yoiboi || 0)
                          if (soldPcs > 0) {
                            const breakdownParts = []
                            if (soldLocal > 0) breakdownParts.push(`Lokal: ${formatNumber(soldLocal)}`)
                            if (soldYoiboi > 0) breakdownParts.push(`Online: ${formatNumber(soldYoiboi)}`)
                            const breakdown = breakdownParts.join(", ")
                            return (
                              <span
                                className="ml-2 px-1.5 py-0.5 text-xs rounded border bg-sky-50 text-sky-700 border-sky-200"
                                title={`Terjual ${formatNumber(soldPcs)} pcs (Order PAID/CONFIRMED/DONE) sejak freeze${breakdown ? ` — ${breakdown}` : ""}`}
                              >
                                Terjual {formatNumber(soldPcs)} pcs
                                {breakdown && (
                                  <span className="ml-1 text-sky-600 font-normal">({breakdown})</span>
                                )}
                              </span>
                            )
                          }
                          // Fallback: stock movement bukan dari sales (mis. masuk GR/transfer).
                          if (r.had_transaction) {
                            const delta = Number(r.frozen_current || 0) - Number(r.current_now || 0)
                            if (delta < 0) {
                              return (
                                <span
                                  className="ml-2 px-1.5 py-0.5 text-xs rounded border bg-violet-50 text-violet-700 border-violet-200"
                                  title={`Saat freeze: ${r.frozen_current} → ${r.current_now}`}
                                >
                                  Masuk {formatNumber(Math.abs(delta))} pcs
                                </span>
                              )
                            }
                          }
                          return null
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-neutral-700">{formatNumber(r.frozen_actual)}</td>
                    <td className="px-3 py-2 text-right text-neutral-700">{formatMoney(r.nilai_computer)}</td>
                    <td className="px-3 py-2 text-right font-medium text-neutral-900">{formatNumber(r.physical)}</td>
                    <td className="px-3 py-2 text-right text-neutral-700">{formatMoney(r.nilai_fisik)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-medium">{r.lebih > 0 ? formatNumber(r.lebih) : "0"}</td>
                    <td className="px-3 py-2 text-right text-rose-700 font-medium">{r.kurang > 0 ? formatNumber(r.kurang) : "0"}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-neutral-50">
              <tr className="border-t border-neutral-200 font-semibold">
                <td className="px-3 py-3" colSpan={2}>
                  TOTAL (filter, {formatNumber(meta.total)} item)
                </td>
                <td className="px-3 py-3 text-right">{formatNumber(summaryFiltered.total_computer)}</td>
                <td className="px-3 py-3 text-right">{formatMoney(summaryFiltered.total_nilai_computer)}</td>
                <td className="px-3 py-3 text-right">{formatNumber(summaryFiltered.total_fisik)}</td>
                <td className="px-3 py-3 text-right">{formatMoney(summaryFiltered.total_nilai_fisik)}</td>
                <td className="px-3 py-3 text-right text-emerald-700">{formatNumber(summaryFiltered.total_lebih_qty)}</td>
                <td className="px-3 py-3 text-right text-rose-700">{formatNumber(summaryFiltered.total_kurang_qty)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {meta.last_page > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-200">
            <div className="text-xs text-neutral-500">
              Halaman {meta.page} dari {meta.last_page}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => mergeParams({ page: 1 })}
                disabled={meta.page <= 1}
                className="px-2.5 py-1.5 text-xs font-medium text-neutral-700 border rounded-lg border-neutral-300 disabled:opacity-40"
              >«</button>
              <button
                type="button"
                onClick={() => mergeParams({ page: Math.max(1, meta.page - 1) })}
                disabled={meta.page <= 1}
                className="px-2.5 py-1.5 text-xs font-medium text-neutral-700 border rounded-lg border-neutral-300 disabled:opacity-40"
              >‹</button>
              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span key={`gap-${i}`} className="px-2 text-xs text-neutral-400">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => mergeParams({ page: p })}
                    className={`min-w-[2rem] px-2.5 py-1.5 text-xs font-medium rounded-lg border ${
                      p === meta.page
                        ? "bg-brand-900 text-white border-brand-900"
                        : "text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => mergeParams({ page: Math.min(meta.last_page, meta.page + 1) })}
                disabled={meta.page >= meta.last_page}
                className="px-2.5 py-1.5 text-xs font-medium text-neutral-700 border rounded-lg border-neutral-300 disabled:opacity-40"
              >›</button>
              <button
                type="button"
                onClick={() => mergeParams({ page: meta.last_page })}
                disabled={meta.page >= meta.last_page}
                className="px-2.5 py-1.5 text-xs font-medium text-neutral-700 border rounded-lg border-neutral-300 disabled:opacity-40"
              >»</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Link
          to={`/stock-opname/${id}`}
          className="px-4 py-2 text-sm font-medium text-neutral-700 border rounded-lg border-neutral-300 bg-white"
        >
          Kembali ke Freeze
        </Link>
      </div>
    </div>
  )
}

function SummaryCard({ label, value, tone }) {
  const toneClass =
    tone === "lebih"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "kurang"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-neutral-200 bg-neutral-50 text-neutral-700"
  return (
    <div className={`p-3 border rounded-xl ${toneClass}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold mt-1">{value}</div>
    </div>
  )
}

function buildPageList(current, last) {
  if (last <= 7) {
    return Array.from({ length: last }, (_, i) => i + 1)
  }
  const pages = new Set([1, 2, last - 1, last, current - 1, current, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b)
  const out = []
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i])
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("...")
  }
  return out
}
